/**
 * The one place a content string becomes a sentence.
 *
 * Every player-facing string in `content/` is ICU MessageFormat (Q12.1), so
 * plural rules, number formats and word order belong to the translator rather
 * than to a `+` in a component. Nothing in this package concatenates strings.
 *
 * `intl-messageformat` compiles a message into a formatter, which is the
 * expensive part; the cache below means each (locale, message) pair is compiled
 * once for the life of the process. The cache is keyed by the message text
 * rather than by its content id, so two locales sharing a message -- common
 * where a locale falls back to English -- still share one compiled formatter.
 */

import { IntlMessageFormat } from 'intl-messageformat'

/**
 * Values a `{placeholder}` may be given. Dates are deliberately absent: the
 * game has no clock finer than a UTC day (`docs/anonymity.md` R4).
 */
export type FormatArgs = Readonly<Record<string, string | number | boolean>>

const cache = new Map<string, IntlMessageFormat>()

const cacheKey = (locale: string, message: string) => `${locale} ${message}`

/**
 * Format an ICU message in `locale`.
 *
 * A malformed message is a content bug, not a player-visible crash: the raw
 * message is returned so the screen still renders, and the content validation
 * test is what fails loudly.
 */
export function format(locale: string, message: string, args?: FormatArgs): string {
  const key = cacheKey(locale, message)
  let formatter = cache.get(key)
  if (formatter === undefined) {
    try {
      formatter = new IntlMessageFormat(message, locale)
    } catch {
      return message
    }
    cache.set(key, formatter)
  }
  try {
    const formatted = formatter.format(args)
    return typeof formatted === 'string' ? formatted : String(formatted)
  } catch {
    return message
  }
}

/** True when `message` compiles as an ICU message. Used by the content tests. */
export function isFormattable(locale: string, message: string): boolean {
  try {
    new IntlMessageFormat(message, locale)
    return true
  } catch {
    return false
  }
}

/** Drop every compiled formatter. Only useful to tests that measure the cache. */
export function clearFormatCache(): void {
  cache.clear()
}

/** How many compiled formatters are held. Exposed for the cache test. */
export function formatCacheSize(): number {
  return cache.size
}
